<template>
  <div class="status">
    <slot v-if="$slots?.close" name="close"></slot>
    <div class="wrapper">
      <div
        v-if="
          [EXECUTION_STATUS.DATA_INIT, EXECUTION_STATUS.PROCESSING].includes(
            dataSource.status
          )
        "
      >
        <a-progress
          v-if="dataSource?.percent !== undefined"
          type="circle"
          :size="60"
          :percent="Math.round(dataSource.percent)"
        />
        <iconpark-icon v-else name="loading" size="42" class="animate-spin">
        </iconpark-icon>
        <div class="title">{{ dataSource.message }}</div>
      </div>
      <div v-else-if="dataSource.status === EXECUTION_STATUS.ERROR">
        <iconpark-icon name="close-fill" size="32"></iconpark-icon>
        <div class="title">
          <span>
            {{ dataSource?.message }}
          </span>
          <span v-if="dataSource?.detail" class="text-font-60">
            <a-popover placement="bottom">
              <template #content>
                {{ dataSource?.detail }}
                <span v-if="isTeamNormal">
                  {{ $t('key-0') }}
                  {{ $t('qing-lian-xi-guan-li-yuan-sheng-ji-hou-shang-chuan') }}
                </span>
              </template>
              <iconpark-icon
                name="info-circle"
                size="14"
                class="align-middle ml-[4px] mb-[3px]"
                fill="#696F78"
              ></iconpark-icon>
            </a-popover>
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="
        dataSource?.resourceType !== undefined &&
        rights.length > 0 &&
        dataSource?.reason === REASON_MAP.RESTRICTED_SIZE &&
        dataSource?.hint &&
        ![
          RESOURCE_TYPE_CONFIG.MUSIC,
          RESOURCE_TYPE_CONFIG.SOUND_EFFECT,
          RESOURCE_TYPE_CONFIG.FONT,
        ].includes(dataSource?.resourceType)
      "
      class="upgrade-tip flex-y-center"
    >
      <div class="pt-[3px]">
        <iconpark-icon name="diamond3d" size="16"></iconpark-icon>
      </div>
      <div class="content">
        <span>{{ $t('sheng-ji-zhi') }}</span>
        <a-popover
          v-for="(right, index) of rights"
          :key="right.id"
          placement="bottom"
          destroy-tooltip-on-hide
        >
          <a
            v-if="isNotCompany(right)"
            href="javascript:;"
            @click="handleUpgrade(right)"
          >
            {{ right.rights_name }}
            {{ index < rights.length - 1 ? '、' : '' }}
          </a>
          <template v-else>
            <a href="javascript:;" @click="handleUpgrade(right)"> 联系我们 </a>
            升级企业版
          </template>
          <template #content>
            <div
              class="w-[80px] text-12 text-font-50 flex-center flex-wrap gap-[4px_8px]"
            >
              <span
                v-for="item of RESOURCE_TIPS()[dataSource?.resourceType]"
                :key="item.limit"
              >
                {{ item.label }} ≤ {{ transSize(right[item.limit]) }}M
              </span>
            </div>
          </template>
        </a-popover>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { USER_LEVEL_KEY, type IEquity } from '~/api/interface'
import { RESOURCE_TIPS, transSize } from '~/hooks/useUpload'
import { getRightsView } from '~/pages/subscribe/components/rights-view'
import Rights10 from '~/pages/subscribe/components/rights-view/rights-10'
import Rights16 from '~/pages/subscribe/components/rights-view/rights-16'
import Rights24 from '~/pages/subscribe/components/rights-view/rights-24'

import { USER_ADMIN } from '~/constants'
import { PageKey } from '~/constants/page'

const props = withDefaults(
  defineProps<{
    dataSource?: any
  }>(),
  {
    dataSource: () => ({}),
  }
)

const userInfo: any = useUserInfo()

const isTeamNormal = computed(() => {
  if (
    [USER_LEVEL_KEY.TEAM_LIGHT, USER_LEVEL_KEY.TEAM_TASTE].includes(
      userInfo.value!.rights_type
    ) &&
    userInfo.value!.permission === USER_ADMIN.NORMAL
  ) {
    return true
  }

  return false
})

const isNotCompany = (item: IEquity) =>
  ![USER_LEVEL_KEY.ORG_STANDARD, USER_LEVEL_KEY.ORG_FLAGSHIP].includes(
    item.rights_type
  )

const rights = computed(
  () =>
    props.dataSource?.rights.filter((item: any) => {
      const company = [
        USER_LEVEL_KEY.ORG_STANDARD,
        USER_LEVEL_KEY.ORG_FLAGSHIP,
        USER_LEVEL_KEY.TEAM_TASTE,
      ]
      const isOrg = company.includes(userInfo.value.rights_type)
      // 团队体验版、企业标准、旗舰不显示内容
      return company.includes(item.rights_type)
        ? false
        : item.rights_type > (isOrg ? 24 : userInfo.value.rights_type)
    }) || []
)

const rightsConfig = useRightsConfig()
const accountList = useAccountList()
const route = useRoute()
const handleUpgrade = (right: IEquity) => {
  const rightsView = getRightsView({
    rights: right,
    userInfo: userInfo.value,
    accountList: accountList.value,
    overseas: isGlobalVersion() ? rightsConfig.value : null,
  })

  if (rightsView instanceof Rights10) {
    rightsView.newOrder()
  }

  if (rightsView instanceof Rights16) {
    rightsView.newOrder()
  }

  if (rightsView instanceof Rights24) {
    if (route.name === PageKey.HOME_PERSONAL) {
      rightsView.openContact('yygw-grkj-scan')
    }
    if (route.name === PageKey.HOME_TEAM) {
      rightsView.openContact('yygw-gxtdkjtsc-sjqy')
    }
  }
}
</script>
<style lang="scss" scoped>
.status {
  @apply absolute inset-0 w-full h-full flex flex-col;
  border-radius: 10px 10px 0px 0px;
  backdrop-filter: blur(0px);

  .wrapper {
    @apply w-full flex-1 flex justify-center items-center z-10 bg-back-3 text-center;
    .title {
      @apply mt-[5px] text-14 text-font-80 font-medium;
    }

    .description {
      @apply my-[8px] text-12 text-font-50;
    }
  }

  .upgrade-tip {
    @apply w-full p-[6px_12px] bg-purple-2 flex flex-nowrap gap-[8px];

    .content {
      @apply text-12 leading-[16px] text-font-1 text-start;
    }
  }
}
</style>
